import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  ArrowLeft, 
  Save, 
  RotateCcw, 
  Layout, 
  Type, 
  BarChart3, 
  Grid3X3, 
  DollarSign,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const CMSEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    hero: true,
    stats: false,
    features: false,
    howItWorks: false,
    pricing: false,
    cta: false,
    footer: false
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await api.get('/cms/landing-page');
      setContent(res.data);
    } catch (err) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/landing-page', content);
      toast.success('Content saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all content to defaults? This cannot be undone.')) {
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/cms/landing-page/reset');
      setContent(res.data.content);
      toast.success('Content reset to defaults');
    } catch (err) {
      toast.error('Failed to reset content');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const updateContent = (path, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split('.');
      let obj = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  const updateFeature = (index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      if (!newContent.features) newContent.features = { features: [] };
      if (!newContent.features.features) newContent.features.features = [];
      if (!newContent.features.features[index]) newContent.features.features[index] = {};
      newContent.features.features[index][field] = value;
      return { ...newContent };
    });
  };

  const addFeature = () => {
    setContent(prev => {
      const newContent = { ...prev };
      if (!newContent.features) newContent.features = { features: [] };
      if (!newContent.features.features) newContent.features.features = [];
      newContent.features.features.push({
        id: String(Date.now()),
        icon: 'Star',
        title: 'New Feature',
        description: 'Feature description',
        color: 'from-blue-500 to-cyan-500'
      });
      return { ...newContent };
    });
  };

  const removeFeature = (index) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.features.features.splice(index, 1);
      return { ...newContent };
    });
  };

  const updatePricingTier = (index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      if (!newContent.pricing) newContent.pricing = { tiers: [] };
      if (!newContent.pricing.tiers) newContent.pricing.tiers = [];
      if (!newContent.pricing.tiers[index]) newContent.pricing.tiers[index] = {};
      newContent.pricing.tiers[index][field] = value;
      return { ...newContent };
    });
  };

  const updatePricingFeature = (tierIndex, featureIndex, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing.tiers[tierIndex].features[featureIndex] = value;
      return { ...newContent };
    });
  };

  const addPricingFeature = (tierIndex) => {
    setContent(prev => {
      const newContent = { ...prev };
      if (!newContent.pricing.tiers[tierIndex].features) {
        newContent.pricing.tiers[tierIndex].features = [];
      }
      newContent.pricing.tiers[tierIndex].features.push('New feature');
      return { ...newContent };
    });
  };

  const removePricingFeature = (tierIndex, featureIndex) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing.tiers[tierIndex].features.splice(featureIndex, 1);
      return { ...newContent };
    });
  };

  const updateHowItWorksStep = (index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      if (!newContent.how_it_works) newContent.how_it_works = { steps: [] };
      if (!newContent.how_it_works.steps) newContent.how_it_works.steps = [];
      if (!newContent.how_it_works.steps[index]) newContent.how_it_works.steps[index] = {};
      newContent.how_it_works.steps[index][field] = value;
      return { ...newContent };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const SectionHeader = ({ title, icon: Icon, section, color }) => (
    <button
      onClick={() => toggleSection(section)}
      className={`w-full flex items-center justify-between p-4 bg-gradient-to-r ${color} rounded-xl text-white font-semibold mb-4`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        {title}
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );

  const InputField = ({ label, value, onChange, multiline = false, placeholder = '' }) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/super-admin/dashboard')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Website Content Editor</h1>
                <p className="text-sm text-slate-500">Edit landing page content</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
              <button
                onClick={handleReset}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Editor */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Hero Section */}
        <SectionHeader title="Hero Section" icon={Layout} section="hero" color="from-indigo-500 to-purple-500" />
        {expandedSections.hero && content?.hero && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <div className="grid md:grid-cols-2 gap-6">
              <InputField
                label="Badge Text"
                value={content.hero.badge_text}
                onChange={(v) => updateContent('hero.badge_text', v)}
              />
              <InputField
                label="Headline Line 1"
                value={content.hero.headline_1}
                onChange={(v) => updateContent('hero.headline_1', v)}
              />
              <InputField
                label="Headline Line 2 (Highlighted)"
                value={content.hero.headline_2}
                onChange={(v) => updateContent('hero.headline_2', v)}
              />
              <InputField
                label="Primary CTA Button"
                value={content.hero.cta_primary}
                onChange={(v) => updateContent('hero.cta_primary', v)}
              />
            </div>
            <InputField
              label="Description"
              value={content.hero.description}
              onChange={(v) => updateContent('hero.description', v)}
              multiline
            />
            <InputField
              label="Secondary CTA Button"
              value={content.hero.cta_secondary}
              onChange={(v) => updateContent('hero.cta_secondary', v)}
            />
          </div>
        )}

        {/* Stats Section */}
        <SectionHeader title="Stats Section" icon={BarChart3} section="stats" color="from-emerald-500 to-teal-500" />
        {expandedSections.stats && content?.stats && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Stat 1</h4>
                <InputField
                  label="Value"
                  value={content.stats.stat_1_value}
                  onChange={(v) => updateContent('stats.stat_1_value', v)}
                />
                <InputField
                  label="Label"
                  value={content.stats.stat_1_label}
                  onChange={(v) => updateContent('stats.stat_1_label', v)}
                />
              </div>
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Stat 2</h4>
                <InputField
                  label="Value"
                  value={content.stats.stat_2_value}
                  onChange={(v) => updateContent('stats.stat_2_value', v)}
                />
                <InputField
                  label="Label"
                  value={content.stats.stat_2_label}
                  onChange={(v) => updateContent('stats.stat_2_label', v)}
                />
              </div>
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Stat 3</h4>
                <InputField
                  label="Value"
                  value={content.stats.stat_3_value}
                  onChange={(v) => updateContent('stats.stat_3_value', v)}
                />
                <InputField
                  label="Label"
                  value={content.stats.stat_3_label}
                  onChange={(v) => updateContent('stats.stat_3_label', v)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        <SectionHeader title="Features Section" icon={Grid3X3} section="features" color="from-blue-500 to-cyan-500" />
        {expandedSections.features && content?.features && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Section Title"
                value={content.features.section_title}
                onChange={(v) => updateContent('features.section_title', v)}
              />
              <InputField
                label="Title Highlight Word"
                value={content.features.section_title_highlight}
                onChange={(v) => updateContent('features.section_title_highlight', v)}
              />
            </div>
            <InputField
              label="Section Description"
              value={content.features.section_description}
              onChange={(v) => updateContent('features.section_description', v)}
              multiline
            />
            
            <h4 className="font-semibold text-slate-800 mt-6 mb-4">Feature Cards</h4>
            <div className="space-y-4">
              {(content.features.features || []).map((feature, index) => (
                <div key={feature.id || index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-slate-700">Feature {index + 1}</span>
                    <button
                      onClick={() => removeFeature(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <InputField
                      label="Title"
                      value={feature.title}
                      onChange={(v) => updateFeature(index, 'title', v)}
                    />
                    <InputField
                      label="Icon (Lucide name)"
                      value={feature.icon}
                      onChange={(v) => updateFeature(index, 'icon', v)}
                      placeholder="e.g., Brain, BookOpen, Shield"
                    />
                  </div>
                  <InputField
                    label="Description"
                    value={feature.description}
                    onChange={(v) => updateFeature(index, 'description', v)}
                    multiline
                  />
                </div>
              ))}
            </div>
            <button
              onClick={addFeature}
              className="mt-4 flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Feature
            </button>
          </div>
        )}

        {/* How It Works Section */}
        <SectionHeader title="How It Works" icon={Type} section="howItWorks" color="from-orange-500 to-amber-500" />
        {expandedSections.howItWorks && content?.how_it_works && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Section Title"
                value={content.how_it_works.section_title}
                onChange={(v) => updateContent('how_it_works.section_title', v)}
              />
              <InputField
                label="Section Description"
                value={content.how_it_works.section_description}
                onChange={(v) => updateContent('how_it_works.section_description', v)}
              />
            </div>
            
            <h4 className="font-semibold text-slate-800 mt-6 mb-4">Steps</h4>
            <div className="grid md:grid-cols-3 gap-4">
              {(content.how_it_works.steps || []).map((step, index) => (
                <div key={step.id || index} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="font-medium text-slate-700 mb-3 block">Step {index + 1}</span>
                  <InputField
                    label="Title"
                    value={step.title}
                    onChange={(v) => updateHowItWorksStep(index, 'title', v)}
                  />
                  <InputField
                    label="Description"
                    value={step.description}
                    onChange={(v) => updateHowItWorksStep(index, 'description', v)}
                    multiline
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Section */}
        <SectionHeader title="Pricing Section" icon={DollarSign} section="pricing" color="from-pink-500 to-rose-500" />
        {expandedSections.pricing && content?.pricing && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <InputField
                label="Section Title"
                value={content.pricing.section_title}
                onChange={(v) => updateContent('pricing.section_title', v)}
              />
              <InputField
                label="Section Description"
                value={content.pricing.section_description}
                onChange={(v) => updateContent('pricing.section_description', v)}
              />
            </div>
            
            <h4 className="font-semibold text-slate-800 mt-6 mb-4">Pricing Tiers</h4>
            <div className="grid md:grid-cols-3 gap-4">
              {(content.pricing.tiers || []).map((tier, tierIndex) => (
                <div key={tier.id || tierIndex} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <InputField
                    label="Plan Name"
                    value={tier.name}
                    onChange={(v) => updatePricingTier(tierIndex, 'name', v)}
                  />
                  <InputField
                    label="Price"
                    value={tier.price}
                    onChange={(v) => updatePricingTier(tierIndex, 'price', v)}
                  />
                  <InputField
                    label="Description"
                    value={tier.description}
                    onChange={(v) => updatePricingTier(tierIndex, 'description', v)}
                  />
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      checked={tier.popular || false}
                      onChange={(e) => updatePricingTier(tierIndex, 'popular', e.target.checked)}
                      className="rounded text-indigo-600"
                    />
                    <label className="text-sm text-slate-600">Mark as Popular</label>
                  </div>
                  
                  <h5 className="font-medium text-slate-700 text-sm mb-2">Features</h5>
                  <div className="space-y-2">
                    {(tier.features || []).map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => updatePricingFeature(tierIndex, featureIndex, e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded"
                        />
                        <button
                          onClick={() => removePricingFeature(tierIndex, featureIndex)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => addPricingFeature(tierIndex)}
                    className="mt-2 text-xs text-indigo-600 hover:underline"
                  >
                    + Add Feature
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <SectionHeader title="CTA Section" icon={MessageSquare} section="cta" color="from-violet-500 to-purple-500" />
        {expandedSections.cta && content?.cta && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <InputField
              label="CTA Title"
              value={content.cta.title}
              onChange={(v) => updateContent('cta.title', v)}
            />
            <InputField
              label="CTA Description"
              value={content.cta.description}
              onChange={(v) => updateContent('cta.description', v)}
              multiline
            />
            <InputField
              label="Button Text"
              value={content.cta.button_text}
              onChange={(v) => updateContent('cta.button_text', v)}
            />
          </div>
        )}

        {/* Footer Section */}
        <SectionHeader title="Footer" icon={Layout} section="footer" color="from-slate-600 to-slate-800" />
        {expandedSections.footer && content?.footer && (
          <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
            <InputField
              label="Tagline"
              value={content.footer.tagline}
              onChange={(v) => updateContent('footer.tagline', v)}
            />
            <InputField
              label="Copyright Text"
              value={content.footer.copyright}
              onChange={(v) => updateContent('footer.copyright', v)}
            />
            <InputField
              label="Domain"
              value={content.footer.domain}
              onChange={(v) => updateContent('footer.domain', v)}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default CMSEditor;
